import { SandboxContext } from "@/layout/Nav/SandboxSwitcher"
import { IApiKeys } from "@/types/credentials.types"
import ConfirmModal from "@/UI/Modals/ConfirmModal"
import { faEllipsis } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Dropdown, Menu, message, Table } from "antd"
import { DateTime } from "luxon"
import { useTranslation } from "react-i18next"
import React, { useContext } from "react"
import DisplaySecret from "../../components/DisplaySecret"

interface ApiKeysTableProps {
  apiKeys: IApiKeys[]
  deleteApiKeys: (id: string) => Promise<void>
}

const columns = (
  t: (key: string) => string,
  isSandbox: boolean,
  deleteApiKeys: (id: string) => Promise<void>
) => [
  {
    title: t("common.name").toUpperCase(),
    dataIndex: "keyName",
    key: "1",
    width: "10%",
    render: (keyName: string) => {
      return (
        <p className="my-auto whitespace-nowrap text-ellipsis overflow-hidden font-medium">
          {keyName}
        </p>
      )
    },
  },
  {
    title: t("common.token"),
    key: "2",
    dataIndex: "secretKey",
    width: "40%",
    render: (secretKey: string) => {
      return (
        <>
          {isSandbox ? (
            <DisplaySecret secret={secretKey} type="truncate" />
          ) : (
            <p className="my-auto break-all overflow-hidden text-gray-600">{secretKey}</p>
          )}
        </>
      )
    },
  },
  {
    title: t("profile.TeamPeopleTable.lastActive").toUpperCase(),
    key: "3",
    dataIndex: "lastActive",
    width: "15%",
    render: (lastActive: number) => {
      return (
        <p className="my-auto text-ellipsis overflow-hidden font-medium">
          {lastActive > 0 ? DateTime.fromMillis(lastActive).toFormat("dd-MM-yyyy") : "-"}
        </p>
      )
    },
  },
  {
    title: t("common.created").toUpperCase(),
    key: "4",
    dataIndex: "createdAt",
    width: "15%",
    render: (createdAt: number) => {
      return (
        <p className="my-auto whitespace-nowrap text-ellipsis overflow-hidden font-medium">
          {DateTime.fromMillis(createdAt).toFormat("dd-MM-yyyy")}
        </p>
      )
    },
  },
  {
    key: "5",
    dataIndex: "id",
    width: "10%",
    render: (id: string) => {
      const onDelete = async () => {
        try {
          if (!id) {
            message.error("developers.APIKeys.noAPIKeyIdError")
            return
          }
          console.log(id)
          await deleteApiKeys(id)
          message.success(t("developers.APIKeys.deleteSecretKeySuccess"))
        } catch (error) {
          message.error(t("developers.APIKeys.deleteSecretKeyError"))
        }
      }

      return (
        <Dropdown
          trigger={["click"]}
          overlay={
            <Menu id="api-keys-table-dropdown">
              <Menu.Item>
                <ConfirmModal
                  button={<div className="h-full">{t("common.delete")}</div>}
                  content={{
                    type: "danger",
                    title: t("developers.APIKeys.deleteAPIKey"),
                    description: t("developers.APIKeys.deleteAPIKeysConfirmation"),
                    cancelText: t("common.cancel"),
                    okText: t("common.yesDelete"),
                  }}
                  onConfirm={onDelete}
                />
              </Menu.Item>
            </Menu>
          }
        >
          <div className="flex items-center justify-center">
            <FontAwesomeIcon
              className="flex-shrink-0 h-4 text-gray-500 text-sm cursor-pointer"
              icon={faEllipsis}
            />
          </div>
        </Dropdown>
      )
    },
  },
]

const ApiKeysTable: React.FC<ApiKeysTableProps> = ({ apiKeys, deleteApiKeys }) => {
  const { t } = useTranslation()

  const sandbox = useContext(SandboxContext)

  return (
    <Table
      id="api-keys-table"
      className="order-table !mt-0"
      size="small"
      rowKey={"id"}
      scroll={{ x: 600 }}
      dataSource={apiKeys}
      columns={columns(t, sandbox.enabled, deleteApiKeys)}
      pagination={{
        showSizeChanger: false,
        total: apiKeys.length,
      }}
    />
  )
}

export default ApiKeysTable
